import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { iif, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { AppParamsService } from '@services/app.params.service';
import { TranslateService } from '@ngx-translate/core';
import { UUID } from 'angular2-uuid';
import { MemberParams } from '@interfaces/member-params.model';
import { IntendedProviderParams } from '@interfaces/intended-provider-params.model';
import { IntendedProvider } from '@interfaces/intended-provider.model';
import { IntendedProviderPostParams } from '@interfaces/intended-provider-post-params.model';
import { ConfigurationService } from '@services/configuration.service';
import { extend, identity, pickBy } from 'lodash';
import { SearchParamsService } from '@components/+search/search-params.service';
import { Results } from '@interfaces/results.interface';

@Injectable({
  providedIn: 'root',
})
export class IntendedProviderService {
  constructor(
    private http: HttpClient,
    private appParams: AppParamsService,
    private translate: TranslateService,
    private configurationService: ConfigurationService,
    private searchParamsService: SearchParamsService
  ) {}

  /**
   * Determines if the preferred provider card should show on search results.
   * To show: search must have results that are incentivized,
   *  member should have incentives enabled and not be bva enabled.
   * @param results: Results
   * @param incentivesEnabled: boolean
   * @param bvaEnabled: boolean
   * @returns boolean
   */
  public shouldShowCard(
    results: Results,
    incentivesEnabled: boolean,
    bvaEnabled: boolean
  ): boolean {
    if (
      !incentivesEnabled ||
      !results._meta?.counts?.total ||
      !results._meta?.cost?.average?.incentive_amount?.upper ||
      bvaEnabled
    ) {
      return false;
    }
    return true;
  }

  /**
   * Get intended providers from referred_provider_autocomplete.json
   * Use IntendedProviderParams if passed in (currently set from EOC carepath-provider component),
   *  otherwise use last search params
   * @param params?: IntendedProviderParams
   * @returns Observable<IntendedProvider[]>
   */
  public getProviders(
    params?: IntendedProviderParams
  ): Observable<IntendedProvider[]> {
    return this.requestProviders(params).pipe(
      map((response) => response?.providers || []),
      map((providers) =>
        providers.map((provider) => new IntendedProvider(provider))
      )
    );
  }

  /**
   * Posts intended/referred provider using the cash_reward_reference_number
   * @param provider: IntendedProvider
   * @returns Observable
   */

  public postIntendedProvider(provider: IntendedProvider): Observable<any> {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (!provider.cash_reward_reference_number) {
      return of(null);
    }
    const url = `/api/incentivized_searches/${provider.cash_reward_reference_number}/referred_provider`;
    return this.http
      .post(url, this.getPostPayload(provider), {
        headers: headers,
        withCredentials: true,
      })
      .pipe(catchError((error) => of(error)));
  }

  private requestProviders(params?: IntendedProviderParams): Observable<any> {
    return iif(
      // If intended provider params were passed in
      () => !!params,
      // Request with IntendedProviderParams
      this.request(this.getHttpRequestParams(params)),
      // Else request with last search params
      this.searchParamsService.lastSearchParams.pipe(
        filter((lastSearchParams: HttpParams) => !!lastSearchParams),
        switchMap((lastSearchParams) => {
          lastSearchParams = lastSearchParams.delete('limit');
          lastSearchParams = lastSearchParams.delete('page');
          return this.request(lastSearchParams);
        })
      )
    );
  }

  private request(params: HttpParams): Observable<any> {
    const url = `/api/search/referred_provider_autocomplete.json`;
    return this.http
      .get(url, {
        params: params,
        withCredentials: true,
      })
      .pipe(catchError(() => of([])));
  }

  private getHttpRequestParams(params: IntendedProviderParams): HttpParams {
    params = extend({}, params);
    extend(params, this.getMemberParams());
    params.locale = this.getLocale();
    params.transaction_id = UUID.UUID();
    params.account_id = this.configurationService.account_id.getValue();
    params = new IntendedProviderParams(params);
    params = pickBy(params, identity);
    return new HttpParams({ fromObject: params as any });
  }

  private getPostPayload(
    provider: IntendedProvider
  ): IntendedProviderPostParams {
    const params = extend({}, provider, this.getMemberParams());
    return new IntendedProviderPostParams(params);
  }

  private getMemberParams(): MemberParams {
    let params = this.appParams.getMemberParams();
    params = new MemberParams(params);
    return params;
  }

  private getLocale(): string {
    let lang = 'en';
    if (this.translate && this.translate.currentLang) {
      lang = this.translate.currentLang;
    }
    return lang;
  }
}
